import { type FC, useId, useMemo, useState } from "react";

interface SuggestionInputProps {
  label: string;
  value: string;
  onChange: (newValue: string) => void;
  /** suggestions are the existing values, the user can pick one or type a new value. */
  suggestions: string[];
  /** newValueHint is shown when the typed value is not one of the suggestions. */
  newValueHint?: string;
  focus?: boolean;
}

const maxVisibleSuggestions = 8;

/** SuggestionInput is a text input that suggests existing values while typing, but also accepts a new value. */
export const SuggestionInput: FC<SuggestionInputProps> = (props) => {
  const inputId = useId();
  const [hasFocus, setHasFocus] = useState(false);
  const [highlighted, setHighlighted] = useState(0);

  const query = props.value.trim().toLowerCase();
  const isExisting = props.suggestions.some((s) => s.toLowerCase() === query);
  const matches = useMemo(
    () =>
      props.suggestions
        .filter((s) => s.toLowerCase().includes(query) && s.toLowerCase() !== query)
        .slice(0, maxVisibleSuggestions),
    [props.suggestions, query],
  );
  const open = hasFocus && matches.length > 0;

  const select = (suggestion: string) => {
    props.onChange(suggestion);
    setHasFocus(false);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlighted((h) => Math.min(h + 1, matches.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      select(matches[Math.min(highlighted, matches.length - 1)]);
    } else if (event.key === "Escape") {
      event.stopPropagation();
      setHasFocus(false);
    }
  };

  return (
    <div className={"flex flex-col text-sm font-medium text-brand-900 w-full max-w-full"}>
      <label htmlFor={inputId}>{props.label}</label>
      <div className={"relative mt-1"}>
        <input
          id={inputId}
          type={"text"}
          autoComplete={"off"}
          autoFocus={props.focus}
          role={"combobox"}
          aria-expanded={open}
          aria-autocomplete={"list"}
          className={
            "border-2 border-slate-200 outline-brand-700 text-black rounded-sm text-base bg-white font-normal px-2 h-10 w-full"
          }
          value={props.value}
          onChange={(e) => {
            props.onChange(e.target.value);
            setHighlighted(0);
          }}
          onFocus={() => setHasFocus(true)}
          onBlur={() => setHasFocus(false)}
          onKeyDown={onKeyDown}
        />
        {open && (
          <ul
            className={
              "absolute top-10 left-0 z-10 w-full max-h-55 overflow-y-auto rounded-sm bg-white text-black shadow-sm"
            }
          >
            {matches.map((suggestion, index) => (
              <li key={suggestion}>
                <button
                  type={"button"}
                  tabIndex={-1}
                  className={`h-10 w-full px-4 text-left font-normal hover:bg-slate-100 ${index === highlighted ? "bg-slate-100" : ""}`}
                  // mousedown instead of click, the input would lose focus (and close the list) before a click lands.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    select(suggestion);
                  }}
                >
                  {suggestion}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {props.newValueHint && query !== "" && !isExisting && (
        <span className={"mt-1 text-xs font-normal text-slate-500"}>{props.newValueHint}</span>
      )}
    </div>
  );
};
